// ============================================================================
// SIR 2026 — Special Intensive Revision voter search
//
// A deliberately minimal, self-contained screen: filters + search box + table.
// Shares NOTHING with the campaign app except the API client and the logged-in
// user, so none of the panel / permission / printing machinery applies.
//
// Backed by SIRVoterList + SIRPart in the election API. Those routes exist ONLY
// under /api/, so every call here passes { open: true } to the API client.
//
// To remove the whole feature: delete this folder, the /sir <Route> in App.js,
// and the "SIR 2026" button in SmartSearchNavbar.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FetchFromServer } from "../../api/ServerApi";
import { CAMPAIGN_SEARCH_ENABLED, HOME_ROUTE, LOGIN_ROUTE } from "../../appMode";
import {
  reasonMeta,
  SIR_API,
  SIR_ASSEMBLY_NAME,
  SIR_ASSEMBLY_NO,
  SIR_COLUMN_LABELS,
  SIR_COLUMN_ORDER,
  SIR_COLUMN_WIDTHS,
  SIR_HIDDEN_COLUMNS,
  SIR_PAGE_SIZE,
  SIR_REASON_FIELD,
  SIR_REASONS,
  SIR_SEARCH_LIMIT,
  SIR_SOURCES,
  SIR_DEFAULT_SOURCE,
  SIR_THEME,
  SIR_BACKGROUND_STYLE,
  SIR_BACKGROUND_STYLE_MOBILE,
  SIR_BANNER_MOBILE_MAX_WIDTH,
  SIR_MOBILE_LENGTH,
  SIR_MOBILE_MAX_VOTERS,
  sirVerdict,
  sirShareMessage,
  sirShortStation,
  parseSirQuery,
  describeSirQuery,
} from "./sirConfig";
import { openWhatsApp } from "../../utils/whatsapp";
import SirAccessModal from "./SirAccessModal";
import {
  clearSirSession,
  fetchQuota,
  fetchSearchHistory,
  isQuotaBlock,
  quotaBlockMessage,
  quotaSummary,
  withIdentity,
} from "./sirQuotaClient";
import SirPartsReport from "./SirPartsReport";

/* ============================ helpers ============================ */

/**
  * The SIR routes are only registered under /api/, never /apisecure/.
  * `open: true` makes the client pick the /api/ base itself — do NOT build the
  * URL here, because a relative base ("api/") would then get BASE_URL prepended
  * again and produce "apisecure/api/Foo".
  */
const sirCall = (apiName, payload) =>
  FetchFromServer(apiName, payload, { plain: true, open: true });

/**
 * Every metered SIR route (the searches, the combined lookup)
 * must carry the caller's identity — device fingerprint plus the signed OTP
 * token — or the server refuses it. `withIdentity` adds those; the counting
 * itself is done server-side, see sirQuotaClient.js.
 */
const meteredCall = async (apiName, payload) =>
  sirCall(apiName, await withIdentity(payload));

/**
 * Columns come from the keys actually returned, ordered by SIR_COLUMN_ORDER,
 * so a schema change on the server still renders something sensible.
 */
function deriveColumns(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const present = new Set();
  rows.forEach((row) => {
    if (row && typeof row === "object") {
      Object.keys(row).forEach((k) => {
        if (!SIR_HIDDEN_COLUMNS.has(k)) present.add(k);
      });
    }
  });

  const ordered = SIR_COLUMN_ORDER.filter((k) => present.has(k));
  const rest = [...present].filter((k) => !SIR_COLUMN_ORDER.includes(k)).sort();
  return [...ordered, ...rest];
}

/**
 * The SIR handlers answer HTTP 200 even on failure, using
 * `{status:'500', message}` — and `[{message}]` for "no filter given".
 * Returns { rows, message }.
 */
function unwrap(data) {
  if (Array.isArray(data)) {
    if (data.length === 1 && data[0] && typeof data[0].message === "string" && !data[0].CardNo) {
      return { rows: [], message: data[0].message };
    }
    return { rows: data, message: "" };
  }
  if (data && typeof data === "object" && data.message) {
    return { rows: [], message: String(data.message) };
  }
  return { rows: [], message: "The server returned an unexpected response." };
}

/** One label/value pair in the detail panel. Renders a dash when unknown. */
function Detail({ label, value, strong, wide }) {
  const text =
    value == null || String(value).trim() === "" ? "—" : String(value);
  return (
    <div style={wide ? { gridColumn: "1 / -1" } : undefined}>
      {/* Label above the value rather than beside it: a polling-station name
          wrapping under its own label reads far better than one wrapping
          around a "2026 polling station:" prefix. */}
      <div style={{ color: "#7b8794", fontSize: 11 }}>{label}</div>
      <div
        style={{
          fontWeight: strong ? 700 : 500,
          fontSize: strong ? 14.5 : 13.5,
          color: "#12263f",
          lineHeight: 1.35,
          wordBreak: "break-word",
        }}
      >
        {text}
      </div>
    </div>
  );
}

function ReasonBadge({ value }) {
  const meta = reasonMeta(value);
  return (
    <span
      style={{
        background: meta.color,
        color: "#fff",
        padding: "2px 8px",
        borderRadius: 10,
        fontSize: 11,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      {meta.label}
    </span>
  );
}

/**
 * Below this many exact matches, the search is re-run with the server's
 * sound-alike matcher, which returns a superset. Above it the result set is
 * already long enough that more near-names would only be noise.
 */
const SIR_FUZZY_THRESHOLD = 50;

/* ============================ component ============================ */

function SirSearchPage() {
  const navigate = useNavigate();

  const userId = localStorage.getItem("UserId");
  const userName = [localStorage.getItem("Surname"), localStorage.getItem("Name")]
    .filter(Boolean)
    .join(" ");
  // Same check App.js uses. Convenience gate only — the register route is open,
  // exactly like every other SIR route.
  const isSuperAdmin = localStorage.getItem("SuperAdmin") === "1";

  /* Staff controls: the dataset selector, the part / reason filters and the raw
   * column table. A voter looking up their own name needs none of that, and
   * every extra control is one more thing to get wrong — so the page opens
   * plain and remembers whichever mode the browser last used. Staff (a signed-in
   * account or the super admin) get the tools switched on by default. */
  const [advanced, setAdvanced] = useState(() => {
    const saved = localStorage.getItem("SirAdvanced");
    if (saved != null) return saved === "1";
    return isSuperAdmin || !!localStorage.getItem("UserId");
  });
  const toggleAdvanced = () =>
    setAdvanced((v) => {
      localStorage.setItem("SirAdvanced", v ? "0" : "1");
      return !v;
    });

  const [query, setQuery] = useState("");
  const [yadibhag, setYadibhag] = useState("");
  const [reason, setReason] = useState("");
  // Which of the two SIR datasets the search box queries.
  const [source, setSource] = useState(SIR_DEFAULT_SOURCE);

  const [parts, setParts] = useState([]);            // enumeration parts
  const [draftParts, setDraftParts] = useState([]);  // draft-roll parts
  const [reasonCounts, setReasonCounts] = useState([]);

  const [rows, setRows] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  // Keyboard cursor into `rows` (NOT into the current page), so Down at the
  // bottom of a page walks onto the next page instead of stopping dead.
  const [activeIndex, setActiveIndex] = useState(-1);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  // --- SIR detail for the clicked row ---
  const [selectedRow, setSelectedRow] = useState(null);
  // Both SIR datasets for the clicked voter, in one call.
  const [combined, setCombined] = useState(null);

  // --- mobile number captured for the clicked voter (SIRVoterMobile) ---
  const [mobile, setMobile] = useState("");          // what is in the input
  const [savedMobile, setSavedMobile] = useState(null); // what is on the server
  const [mobileBusy, setMobileBusy] = useState(false);
  // "Copied ✓" acknowledgement on the copy-to-clipboard button.
  const [copied, setCopied] = useState(false);
  const [mobileNote, setMobileNote] = useState(null);   // { kind, text }

  // --- super-admin register of every number collected ---
  const [showParts, setShowParts] = useState(false);
  // --- free / OTP / credit allowance (server-decided, see sirQuotaClient) ---
  const [quota, setQuota] = useState(null);
  // The refusal that opened the wall, so the modal can explain WHICH limit hit.
  const [quotaBlock, setQuotaBlock] = useState(null);
  const [showAccess, setShowAccess] = useState(false);

  // --- "My searches": the permanent record of what this number looked at ---
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");

  const [showRegister, setShowRegister] = useState(false);
  const [registerRows, setRegisterRows] = useState([]);
  const [registerSearch, setRegisterSearch] = useState("");
  const [registerLoading, setRegisterLoading] = useState(false);

  const inputRef = useRef(null);
  const tableRef = useRef(null);
  // What the box held when the results on screen were fetched, so a second
  // Enter can tell "same search" from "new search".
  const lastSearched = useRef(null);

  /* -------- sign out = become an anonymous visitor again --------
   * Clears BOTH identities: the campaign session keys the navbar clears, and
   * the signed SIR token that carries the OTP / credit tier. The user is left
   * on this page as a free visitor rather than sent to a login screen — being
   * signed out of a public search should not look like being thrown out of it.
   *
   * WARNING: "new visitor" does NOT mean "five more free searches". The free
   * tier is metered by device fingerprint and IP on the server, so a device
   * that has spent its five still has none left. That is the point of the
   * meter; signing out must not be a way around it.
   */
  const handleLogout = () => {
    if (!window.confirm("Sign out? You will go back to the free search.")) return;
    [
      "UserId",
      "MobileNumber",
      "Password",
      "OtpVerified",
      "Name",
      "Surname",
      "SuperAdmin",
      "ThemeColor",
    ].forEach((k) => localStorage.removeItem(k));
    clearSirSession();
    // A full reload is deliberate: MainPage, the navbar and the sidebar all
    // read these keys at mount, so re-rendering in place would leave stale
    // state behind. Landing back here keeps the search available.
    window.location.href = HOME_ROUTE;
  };

  /* -------- the public SIR search is open to everyone --------
   * This screen is the free/paid public product now: an anonymous visitor gets
   * 5 voters, then OTP, then credits (api_files/sirQuota.js). So there is no
   * login redirect here any more — a missing UserId is the NORMAL case, and
   * the backend meters the visitor by device fingerprint + IP instead.
   */

  // Allowance on load, so the badge is correct before the first search.
  useEffect(() => {
    let cancelled = false;
    fetchQuota()
      .then((q) => {
        if (!cancelled) setQuota(q);
      })
      .catch((err) => {
        // Non-fatal: the badge stays blank and the server still enforces.
        console.error("Quota lookup failed", err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Server said no.
   *
   * Deliberately does NOT open the modal. Being refused should leave the
   * search page exactly where it is, with a notice saying why and a button to
   * fix it — not a dialog thrown over the top. Searching the list stays free,
   * so the page is still useful while the notice is up.
   */
  const openAccessWall = useCallback((res) => {
    setQuotaBlock(res);
    if (res && res.quota) setQuota(res.quota);
  }, []);

  /**
   * The user's own activity log. Comes from SIRSearchAccess, which records
   * EVERY search and every voter opened — including repeats that cost nothing,
   * which the billing ledger deliberately does not keep.
   */
  const loadHistory = useCallback(async () => {
    setShowHistory(true);
    setHistoryLoading(true);
    setHistoryError("");
    try {
      const res = await fetchSearchHistory();
      if (res && res.status === "ok") setHistory(res);
      else setHistoryError(res?.message || "Could not load your searches.");
    } catch (err) {
      console.error("Search history failed", err);
      setHistoryError("Could not reach the server.");
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  /* -------- Escape: close the detail panel, then clear the search box -------
   * Ordered deliberately. The detail panel covers part of the results, so the
   * first Escape should dismiss that; only once it is gone does Escape fall
   * through to emptying the search box. Doing both at once would make one
   * keypress lose the operator's query as well.
   * Registered on window so it works while the caret is in the input.
   */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;

      // An overlay is in front: it owns Escape. Both listeners are on window,
      // so without this guard closing a dialog would also clear the search box
      // underneath it - one keypress, two unrelated effects.
      if (showParts || showRegister || showHistory || showAccess) return;

      if (selectedRow) {
        e.preventDefault();
        closeDetail();
        return;
      }
      if (query) {
        e.preventDefault();
        setQuery("");
        setRows([]);
        setPageIndex(0);
        setError("");
        setNotice("");
        setHasSearched(false);
        inputRef.current?.focus();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRow, query, showParts, showRegister, showHistory, showAccess]);

  /* -------- part lists for BOTH datasets (load once) -------- */
  useEffect(() => {
    let cancelled = false;

    sirCall(SIR_API.parts, { constno: SIR_ASSEMBLY_NO })
      .then((data) => {
        if (cancelled) return;
        setParts(unwrap(data).rows);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to load SIR parts", err);
        // Non-fatal: the free-text search still works without the dropdown.
      });

    sirCall(SIR_API.draftParts, { constno: SIR_ASSEMBLY_NO })
      .then((data) => {
        if (cancelled) return;
        setDraftParts(unwrap(data).rows);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to load draft-roll parts", err);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /* -------- reason counts (reload when the part changes) -------- */
  useEffect(() => {
    let cancelled = false;

    const payload = { constno: SIR_ASSEMBLY_NO };
    if (yadibhag) payload.yadibhag = Number(yadibhag);

    sirCall(SIR_API.reasonCounts, payload)
      .then((data) => {
        if (cancelled) return;
        const { rows: list } = unwrap(data);
        setReasonCounts(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to load SIR reason counts", err);
        setReasonCounts([]);
      });

    return () => {
      cancelled = true;
    };
  }, [yadibhag]);

  const columns = useMemo(() => deriveColumns(rows), [rows]);

  const totalPages = Math.max(1, Math.ceil(rows.length / SIR_PAGE_SIZE));
  const pageSlice = useMemo(
    () =>
      rows.slice(
        pageIndex * SIR_PAGE_SIZE,
        pageIndex * SIR_PAGE_SIZE + SIR_PAGE_SIZE
      ),
    [rows, pageIndex]
  );

  const selectedPart = useMemo(
    () => parts.find((p) => String(p.yadibhag) === String(yadibhag)),
    [parts, yadibhag]
  );

  // The Part dropdown and the headline count both follow the selected dataset —
  // the two tables hold different people, so one shared total would be wrong.
  const activeParts = source === "draft" ? draftParts : parts;

  const totalInScope = useMemo(
    () => reasonCounts.reduce((sum, r) => sum + (Number(r.cnt) || 0), 0),
    [reasonCounts]
  );

  const draftTotal = useMemo(
    () => draftParts.reduce((sum, p) => sum + (Number(p.loadedCount) || 0), 0),
    [draftParts]
  );

  const datasetTotal = source === "draft" ? draftTotal : totalInScope;

  const selectedPartTotal = useMemo(() => {
    if (!yadibhag) return null;
    const hit = activeParts.find((p) => String(p.yadibhag) === String(yadibhag));
    if (!hit) return null;
    return source === "draft" ? Number(hit.loadedCount) || 0 : Number(hit.voterCount) || 0;
  }, [activeParts, yadibhag, source]);

  /* -------- SIR detail lookup for one clicked row -------- */
  const selectRow = useCallback(
    async (row) => {
      setSelectedRow(row);
      setCombined(null);
      setMobile("");
      setSavedMobile(null);
      setMobileNote(null);

      const epic = String(row?.CardNo || "").trim().toUpperCase();

      // Any number THIS user already collected for this voter. Numbers are
      // private per karyakarta, so another user's number for the same voter
      // is not returned and the box stays empty. Non-fatal on failure.
      if (epic) {
        sirCall(SIR_API.voterMobile, { CardNo: epic, UserId: Number(userId) || 0 })
          .then((data) => {
            const { rows: list } = unwrap(data);
            const hit = list[0] && list[0].MobileNumber ? list[0] : null;
            setSavedMobile(hit);
            if (hit) setMobile(String(hit.MobileNumber));
          })
          .catch((err) => {
            console.error("Saved mobile lookup failed", err);
          });
      }

      // Both SIR datasets in one call, so the verdict does not need two
      // round-trips.
      // Opening a voter is what a free search / a credit BUYS, so this is the
      // call that is charged. It answers { status, quota, rows } rather than a
      // bare array, so the badge can be refreshed from the same round-trip.
      if (epic && /^[A-Z0-9]+$/.test(epic)) {
        meteredCall(SIR_API.combined, { CardNo: epic, constno: SIR_ASSEMBLY_NO })
          .then((data) => {
            if (isQuotaBlock(data)) {
              setCombined(null);
              setSelectedRow(null);
                      openAccessWall(data);
              return;
            }
            if (data && data.quota) setQuota(data.quota);
            const { rows: list } = unwrap(
              Array.isArray(data) ? data : data?.rows || []
            );
            setCombined(list[0] || {});
          })
          .catch((err) => {
            console.error("Combined SIR lookup failed", err);
            setCombined(null);
          });
      }

    },
    [userId, openAccessWall]
  );

  /* -------- keyboard navigation over the results --------
   * Down / Up      move one row
   * PageDown / Up  move one page
   * Home / End     first / last row
   * Enter          open the highlighted voter
   *
   * Bound to window, not the table, so it works while the caret is still in
   * the search box - the operator types, hits Enter, and can walk the results
   * without ever reaching for the mouse. preventDefault stops Down/Up from
   * moving the text caret or scrolling the page instead.
   *
   * Enter is deliberately NOT handled here: inside the search input it belongs
   * to the form (onSubmit decides search-vs-open), and handling it in both
   * places would fire twice.
   */
  useEffect(() => {
    if (!rows.length) return undefined;

    const onNavKey = (e) => {
      if (e.ctrlKey || e.altKey || e.metaKey) return;

      // Let the mobile-number box and the register's search box work normally.
      const tag = (e.target?.tagName || "").toLowerCase();
      const isTypingField = tag === "input" && e.target !== inputRef.current;
      if (isTypingField || tag === "textarea" || tag === "select") return;

      const last = rows.length - 1;
      let next = null;

      switch (e.key) {
        case "ArrowDown":
          next = Math.min(activeIndex + 1, last);
          break;
        case "ArrowUp":
          next = Math.max(activeIndex - 1, 0);
          break;
        case "PageDown":
          next = Math.min(activeIndex + SIR_PAGE_SIZE, last);
          break;
        case "PageUp":
          next = Math.max(activeIndex - SIR_PAGE_SIZE, 0);
          break;
        case "Home":
          next = 0;
          break;
        case "End":
          next = last;
          break;
        case "Enter":
          // Only when focus is outside the search box; the form owns it there.
          if (e.target === inputRef.current) return;
          if (activeIndex >= 0) {
            e.preventDefault();
            selectRow(rows[activeIndex]);
          }
          return;
        default:
          return;
      }

      e.preventDefault();
      if (next !== activeIndex) setActiveIndex(next);
    };

    window.addEventListener("keydown", onNavKey);
    return () => window.removeEventListener("keydown", onNavKey);
  }, [rows, activeIndex, selectRow]);

  /* Follow the cursor across page boundaries, and keep the highlighted row
   * on screen. Paging is derived from activeIndex rather than tracked
   * separately, so the two can never disagree. */
  useEffect(() => {
    if (activeIndex < 0) return;

    const wantPage = Math.floor(activeIndex / SIR_PAGE_SIZE);
    if (wantPage !== pageIndex) setPageIndex(wantPage);

    // After the page has rendered, pull the row into view.
    const id = window.setTimeout(() => {
      const el = tableRef.current?.querySelector(`[data-rowidx="${activeIndex}"]`);
      el?.scrollIntoView({ block: "nearest" });
    }, 0);
    return () => window.clearTimeout(id);
  }, [activeIndex, pageIndex]);

  /* -------- mobile number: save, then share on WhatsApp -------- */

  // Only digits, never longer than 10 — the length lock is here rather than
  // in a validator so a wrong key simply cannot be typed.
  const onMobileChange = (e) => {
    const digits = String(e.target.value || "").replace(/\D/g, "").slice(0, SIR_MOBILE_LENGTH);
    setMobile(digits);
    setMobileNote(null);
  };

  const mobileIsComplete = mobile.length === SIR_MOBILE_LENGTH;
  const mobileIsSaved = !!savedMobile && String(savedMobile.MobileNumber) === mobile;

  const saveMobile = useCallback(async () => {
    const epic = String(selectedRow?.CardNo || "").trim().toUpperCase();
    if (!epic || mobile.length !== SIR_MOBILE_LENGTH) return false;

    setMobileBusy(true);
    setMobileNote(null);
    try {
      const res = await sirCall(SIR_API.saveMobile, {
        constno: SIR_ASSEMBLY_NO,
        CardNo: epic,
        MobileNumber: mobile,
        ElectorName: selectedRow.ElectorName || null,
        yadibhag: combined?.DraftPart ?? selectedRow.yadibhag ?? null,
        vno: combined?.DraftVno ?? selectedRow.vno ?? null,
        UserId: Number(userId) || 0,
        UserFullName: userName || null,
        DeviceId: localStorage.getItem("DEVICE_ID") || "WEB",
      });

      const body = Array.isArray(res) ? res[0] : res;

      // The cap is the server's call — it counts distinct voters, and a
      // browser-side count could be stale or simply skipped.
      if (body?.status === "limit") {
        // The server does not say WHICH voters, and must not: they may have
        // been collected by a different karyakarta.
        setMobileNote({
          kind: "error",
          text:
            `This number is already used for ${SIR_MOBILE_MAX_VOTERS} voters. ` +
            "Use a different number.",
        });
        return false;
      }

      if (body?.status !== "ok") {
        setMobileNote({ kind: "error", text: body?.message || "Could not save the mobile number." });
        return false;
      }

      setSavedMobile({ CardNo: epic, MobileNumber: mobile });
      setMobileNote({
        kind: "ok",
        text: body.unchanged
          ? "Already saved for this voter."
          : `Saved. This number is now used for ${body.usedBy || 1} of ${SIR_MOBILE_MAX_VOTERS} voters.`,
      });
      return true;
    } catch (err) {
      console.error("Saving the mobile number failed", err);
      setMobileNote({ kind: "error", text: "Could not reach the server to save the number." });
      return false;
    } finally {
      setMobileBusy(false);
    }
  }, [selectedRow, mobile, combined, userId, userName]);

  // Save first, then open WhatsApp — sharing to a number we refused to store
  // would leave the register incomplete, which is the point of collecting it.
  const shareOnWhatsApp = useCallback(async () => {
    if (mobile.length !== SIR_MOBILE_LENGTH) return;

    if (!mobileIsSaved) {
      const ok = await saveMobile();
      if (!ok) return;
    }

    const onDraft = combined ? combined.DraftVno != null : undefined;
    const rsn = combined ? combined.SirReason : selectedRow?.[SIR_REASON_FIELD];
    // No `roll` here on purpose — the shared slip carries SIR data only.
    const message = sirShareMessage({
      row: selectedRow,
      combined,
      verdict: sirVerdict({ onDraft, reason: rsn }),
    });

    openWhatsApp({ mobile, message });
  }, [mobile, mobileIsSaved, saveMobile, selectedRow, combined]);

  // Copy the SAME slip to the clipboard, for the common case: one karyakarta
  // holding one phone, pasting into a chat they already have open instead of
  // bouncing through wa.me for every voter.
  //
  // Gated exactly like the share button — a valid number is entered and SAVED
  // against this voter first — so the details can never be collected without
  // surrendering a real mobile number, and the server's per-number voter cap
  // applies identically. Once saved, copying again is free: the voter is
  // already unlocked, so nothing here is metered.
  const copyDetails = useCallback(async () => {
    if (mobile.length !== SIR_MOBILE_LENGTH) return;

    if (!mobileIsSaved) {
      const ok = await saveMobile();
      if (!ok) return;
    }

    const onDraft = combined ? combined.DraftVno != null : undefined;
    const rsn = combined ? combined.SirReason : selectedRow?.[SIR_REASON_FIELD];
    const message = sirShareMessage({
      row: selectedRow,
      combined,
      verdict: sirVerdict({ onDraft, reason: rsn }),
    });

    // navigator.clipboard is unavailable on plain HTTP and inconsistent inside
    // the APK's WebView, so the old textarea trick stays as the fallback.
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(message);
        ok = true;
      }
    } catch (err) {
      ok = false;
    }

    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = message;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.top = "-1000px";
        document.body.appendChild(ta);
        ta.select();
        ta.setSelectionRange(0, message.length);
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (err) {
        ok = false;
      }
    }

    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } else {
      setMobileNote({
        kind: "error",
        text: "Could not copy. Select the details and copy manually.",
      });
    }
  }, [mobile, mobileIsSaved, saveMobile, selectedRow, combined]);

  /* -------- super-admin register -------- */
  const loadRegister = useCallback(async (search = "") => {
    setRegisterLoading(true);
    try {
      const data = await sirCall(SIR_API.allMobiles, {
        constno: SIR_ASSEMBLY_NO,
        search: search.trim(),
        limit: 2000,
      });
      const { rows: list } = unwrap(data);
      setRegisterRows(list);
    } catch (err) {
      console.error("Loading the mobile register failed", err);
      setRegisterRows([]);
    } finally {
      setRegisterLoading(false);
    }
  }, []);

  const removeRegisterRow = useCallback(
    async (entry) => {
      if (
        !window.confirm(
          `Remove ${entry.MobileNumber} from ${entry.CardNo}?\n\n` +
            "This frees the number so it can be used for another voter."
        )
      )
        return;
      try {
        await sirCall(SIR_API.removeMobile, { SIRMobileId: entry.SIRMobileId });
        setRegisterRows((prev) => prev.filter((r) => r.SIRMobileId !== entry.SIRMobileId));
      } catch (err) {
        console.error("Removing the register entry failed", err);
        alert("Could not remove the entry.");
      }
    },
    []
  );

  const exportRegisterCsv = useCallback(() => {
    const head = ["Mobile", "EPIC", "Name", "Part", "VoterNo", "SavedBy", "UserId", "DateTime"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [
      head.join(","),
      ...registerRows.map((r) =>
        [r.MobileNumber, r.CardNo, r.ElectorName, r.yadibhag, r.vno, r.UserFullName, r.UserId, r.DateTime]
          .map(esc)
          .join(",")
      ),
    ].join("\n");

    const url = URL.createObjectURL(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `sir-voter-mobiles-${SIR_ASSEMBLY_NO}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [registerRows]);

  /* -------- search -------- */
  const runSearch = useCallback(
    async (overrides = {}) => {
      const term = (overrides.query ?? query).trim();
      const part = overrides.yadibhag ?? yadibhag;
      const rsn = overrides.reason ?? reason;
      const srcNow = overrides.source ?? source;

      // The server requires at least one filter — mirror that check here so we
      // don't spend a round-trip on it.
      if (!term && !part && !(rsn && srcNow === "flagged")) {
        setError("Enter a name or EPIC number, or pick a Part" +
                 (srcNow === "flagged" ? " / Reason." : "."));
        return;
      }

      setLoading(true);
      setError("");
      setNotice("");
      setHasSearched(true);

      try {
        const src = overrides.source ?? source;
        const payload = {
          constno: SIR_ASSEMBLY_NO,
          limit: SIR_SEARCH_LIMIT,
        };

        // "141/12/332", "12/332" and a bare EPIC become exact filters; anything
        // else stays free text, where the server matches every word against
        // surname / name / relative / full name, so word order does not matter.
        const parsed = parseSirQuery(term);
        if (parsed.CardNo) payload.CardNo = parsed.CardNo;
        if (parsed.search) payload.search = parsed.search;
        if (parsed.vno != null) payload.vno = parsed.vno;
        // An explicit part in the query wins over the dropdown.
        if (parsed.yadibhag != null) payload.yadibhag = parsed.yadibhag;
        else if (part) payload.yadibhag = Number(part);

        // Reason only exists on the enumeration list; the draft roll has no such column.
        if (rsn && src === "flagged") payload.Reason = rsn;

        const api = src === "draft" ? SIR_API.draftSearch : SIR_API.search;
        const data = await meteredCall(api, payload);

        // The results LIST costs nothing — people must be able to find their
        // own name before being asked to pay. It can still be refused on the
        // daily anti-scraping ceiling.
        if (isQuotaBlock(data)) {
          setRows([]);
          setPageIndex(0);
          setError(quotaBlockMessage(data));
          openAccessWall(data);
          return;
        }

        let { rows: list, message } = unwrap(data);

        // "Smart" name matching. The roll spells it "Aylani", people type
        // "Ailani" — and "Meena"/"Mina", "Kumar"/"Kumaar", "Vijay"/"Bijay" are
        // the same problem. A LIKE prefix can never bridge that, so the search
        // is re-run with fuzzy = 1, where the server matches the exact spelling
        // OR the sound skeleton and returns a SUPERSET of what came back here.
        //
        // The trigger is a SMALL result set, not an empty one. "Ailani Kumar"
        // finds seven genuine Ailanis, so a retry that fired only on zero rows
        // never looked for "Aylani" at all — which is exactly the voter who
        // could not find themselves. A search that already returned plenty is
        // left alone: the extra pass is REGEXP work on the server, and nobody
        // scrolling hundreds of matches needs more of them.
        let fuzzyUsed = false;
        if (payload.search && list.length < SIR_FUZZY_THRESHOLD) {
          const alt = await meteredCall(api, { ...payload, fuzzy: 1 });
          if (!isQuotaBlock(alt)) {
            const fuzzyResult = unwrap(alt);
            if (fuzzyResult.rows.length > list.length) {
              fuzzyUsed = list.length > 0;
              list = fuzzyResult.rows;
              message = fuzzyResult.message;
            }
          }
        }

        setRows(list);
        setPageIndex(0);
        setSelectedRow(null);
          setCombined(null);

        // The first row is the keyboard cursor's home, so Enter straight after
        // a search opens the top match without touching the mouse.
        setActiveIndex(list.length ? 0 : -1);

        // Exactly one match is unambiguous - open it rather than making the
        // operator confirm a choice of one.
        if (list.length === 1) selectRow(list[0]);

        if (message) setNotice(message);
        else if (fuzzyUsed) {
          setNotice(
            `Showing ${list.length} match${list.length === 1 ? "" : "es"} for "${payload.search}", including similar spellings (Ailani / Aylani, Meena / Mina).`
          );
        } else if (list.length >= SIR_SEARCH_LIMIT) {
          setNotice(
            `Showing the first ${SIR_SEARCH_LIMIT} matches. Narrow the search by Part or Reason to see the rest.`
          );
        }
      } catch (err) {
        console.error("SIR search failed", err);
        setRows([]);
        setPageIndex(0);
        setSelectedRow(null);

        const raw = String(err?.message || "");
        if (raw.includes("404") || /Cannot POST/i.test(raw)) {
          setError(
            `SIR data is not connected yet — "${SIR_API.search}" is not available on this server.`
          );
        } else {
          setError(raw || "Search failed. Please check your connection.");
        }
      } finally {
        setLoading(false);
      }
    },
    [query, yadibhag, reason, source, selectRow, openAccessWall]
  );

  // Enter searches; Enter again (query unchanged) opens the highlighted voter.
  // Re-running the same search on a second Enter would just redraw the same
  // rows, so the keypress is spent on the more useful action.
  const onSubmit = (e) => {
    e.preventDefault();
    if (rows.length && query.trim() === lastSearched.current && activeIndex >= 0) {
      selectRow(rows[activeIndex]);
      return;
    }
    lastSearched.current = query.trim();
    runSearch();
  };

  const closeDetail = () => {
    setSelectedRow(null);
    setCombined(null);
  };

  const clearAll = () => {
    setQuery("");
    setYadibhag("");
    setReason("");
    setRows([]);
    setPageIndex(0);
    setActiveIndex(-1);
    lastSearched.current = null;
    setError("");
    setNotice("");
    setHasSearched(false);
    closeDetail();
    inputRef.current?.focus();
  };

  // The banner is fixed artwork, but a 6.8:1 strip is unreadable on a phone
  // (and in the APK), so portrait gets the 4:1 version of the same image.
  const [narrow, setNarrow] = useState(
    typeof window !== "undefined" &&
      window.innerWidth <= SIR_BANNER_MOBILE_MAX_WIDTH
  );
  useEffect(() => {
    const onResize = () =>
      setNarrow(window.innerWidth <= SIR_BANNER_MOBILE_MAX_WIDTH);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // A 28px control is a mouse target, not a thumb target, and anything under
  // 16px makes iOS Safari zoom the whole page when the field is focused. Phones
  // therefore get taller controls and bigger type; desktop keeps the dense look.
  const controlStyle = {
    height: narrow ? 40 : 30,
    padding: narrow ? "4px 10px" : "2px 8px",
    borderRadius: 6,
    border: "1px solid #ccd4df",
    fontSize: narrow ? 15 : 12.5,
    outline: "none",
  };

  // Cards read better than a 10-column table for anyone who is not staff, and
  // are the only thing that fits a phone at all.
  const useCards = narrow || !advanced;

  // Dataset label + headline count, lifted into the header bar so the old
  // full-width summary strip no longer costs a row of height.
  const datasetLabel = SIR_SOURCES.find((o) => o.value === source)?.label;
  const datasetCountText =
    selectedPartTotal != null
      ? `Part ${yadibhag}: ${selectedPartTotal.toLocaleString()}`
      : `${datasetTotal.toLocaleString()} voters`;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
        background: "#fff",
      }}
    >
      {/* ===================== FIXED PARTY BANNER ===================== */}
      <div style={narrow ? SIR_BACKGROUND_STYLE_MOBILE : SIR_BACKGROUND_STYLE} />

      {/* ===================== HEADER ===================== */}
      <div
        style={{
          background: `linear-gradient(90deg, ${SIR_THEME.accent}, ${SIR_THEME.bar})`,
          color: SIR_THEME.barText,
          padding: "5px 10px",
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          boxShadow: "0 1px 4px rgba(13,71,161,0.25)",
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 14, whiteSpace: "nowrap" }}>
          SIR 2026 — Voter Enumeration Search
        </div>

        <div
          style={{
            background: "rgba(255,255,255,0.18)",
            border: "1px solid rgba(255,255,255,0.35)",
            padding: "1px 9px",
            borderRadius: 11,
            fontSize: 11.5,
            fontWeight: 700,
            whiteSpace: "nowrap",
          }}
        >
          AC {SIR_ASSEMBLY_NAME}
        </div>

        {/* Dataset + headline count. Which of the two SIR datasets is being
            queried is a staff concern, so the chip only appears with the rest
            of the tools. */}
        {advanced && (
        <div
          title={datasetLabel}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: "rgba(255,255,255,0.14)",
            border: "1px solid rgba(255,255,255,0.3)",
            borderRadius: 11,
            padding: "1px 9px",
            fontSize: 11.5,
            fontWeight: 700,
            whiteSpace: "nowrap",
          }}
        >
          {/* The dataset name is the label of the dropdown immediately below
              this bar, so repeating it here just costs a phone a line of
              height. Wide screens have room, and the pairing reads well. */}
          {!narrow && (
            <>
              <span style={{ opacity: 0.9 }}>{datasetLabel}</span>
              <span style={{ opacity: 0.5 }}>|</span>
            </>
          )}
          <span>{datasetCountText}</span>
        </div>
        )}

        {advanced && selectedPart && (
          <div
            style={{
              fontSize: 12,
              opacity: 0.95,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: 420,
            }}
            title={selectedPart.PartName || ""}
          >
            📍 Part {selectedPart.yadibhag} — {selectedPart.PartName || "-"}
            {selectedPart.PinCode ? ` (${selectedPart.PinCode})` : ""}
          </div>
        )}

        <div style={{ flex: 1 }} />

        {/* Allowance. Always visible so nobody is surprised by the wall, and
            always the SERVER's number — this is display, not bookkeeping. */}
        {quota && !quota.unlimited && (
          <button
            type="button"
            onClick={() => {
              setQuotaBlock(null);
              setShowAccess(true);
            }}
            style={{
              ...controlStyle,
              border: "1px solid rgba(255,255,255,0.6)",
              background:
                (quota.freeRemaining === 0 && !quota.verified) ||
                  (quota.verified && quota.otpRemaining === 0 && !quota.credits)
                  ? "#c62828"
                  : "rgba(255,255,255,0.14)",
              color: "#fff",
              fontWeight: 700,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
            title={
              quota.verified
                ? "Verified as " + quota.mobile + " — click to buy credits"
                : "Click to verify your mobile number and get 5 more searches"
            }
          >
            {quotaSummary(quota)}
          </button>
        )}

        {userId ? (
          <span style={{ fontSize: 12, whiteSpace: "nowrap" }}>
            {userName} (ID: {userId})
          </span>
        ) : quota?.verified ? (
          // An OTP-verified visitor is "logged in" as far as they are
          // concerned — show the number their allowance is attached to.
          <button
            type="button"
            onClick={loadHistory}
            style={{
              ...controlStyle,
              border: "1px solid rgba(255,255,255,0.6)",
              background: "transparent",
              color: "#fff",
              fontWeight: 600,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
            title="Everything you have searched and opened"
          >
            &#10003; {quota.mobile}
          </button>
        ) : (
          // Staff sign-in. Everyone else can ignore it and just search: the
          // whole point of this screen is that an account is not required.
          <button
            type="button"
            onClick={() => navigate(LOGIN_ROUTE)}
            style={{
              ...controlStyle,
              border: "1px solid rgba(255,255,255,0.6)",
              background: "transparent",
              color: "#fff",
              fontWeight: 600,
              cursor: "pointer",
            }}
            title="For campaign staff with an account"
          >
            Login
          </button>
        )}

        {/* One switch between "just find my name" and the staff filters. Kept
            in the header so it is discoverable but never in the way. */}
        <button
          type="button"
          onClick={toggleAdvanced}
          aria-pressed={advanced}
          style={{
            ...controlStyle,
            border: "1px solid rgba(255,255,255,0.6)",
            background: advanced ? "rgba(255,255,255,0.28)" : "transparent",
            color: "#fff",
            fontWeight: 600,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
          title={
            advanced
              ? "Hide the dataset, part and reason filters"
              : "Show the dataset, part and reason filters"
          }
        >
          {advanced ? "✕ Filters" : "⚙ Filters"}
        </button>

        {/* Only offer the campaign app when it is actually running —
            otherwise /main just bounces straight back here. */}
        {CAMPAIGN_SEARCH_ENABLED && (
          <button
            type="button"
            onClick={() => navigate("/main")}
            style={{
              ...controlStyle,
              border: "1px solid rgba(255,255,255,0.6)",
              background: "transparent",
              color: "#fff",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            ← Voter Search
          </button>
        )}

        {advanced && !narrow && (
        <button
          type="button"
          onClick={() => setShowParts(true)}
          style={{
            ...controlStyle,
            border: "1px solid rgba(255,255,255,0.6)",
            background: "transparent",
            color: "#fff",
            fontWeight: 600,
            cursor: "pointer",
          }}
          title="All parts with voter totals, and where the counts disagree"
        >
          ≡ Parts
        </button>
        )}

        {isSuperAdmin && (
          <>
          <button
            type="button"
            onClick={() => {
              setShowRegister(true);
              loadRegister(registerSearch);
            }}
            style={{
              ...controlStyle,
              border: "1px solid rgba(255,255,255,0.6)",
              background: "transparent",
              color: "#fff",
              fontWeight: 600,
              cursor: "pointer",
            }}
            title="Every mobile number collected against a voter"
          >
            ☎ Mobile register
          </button>
            <button
              type="button"
              onClick={() => navigate("/sir/users")}
              style={{
                ...controlStyle,
                border: "1px solid rgba(255,255,255,0.6)",
                background: "transparent",
                color: "#fff",
                fontWeight: 600,
                cursor: "pointer",
              }}
              title="Everyone using the public search, and their credit balance"
            >
              &#128100; Users
            </button>
          </>
        )}

        {/* Offered to anyone who HAS an identity — a campaign account or an
            OTP-verified number. A plain anonymous visitor has nothing to end. */}
        {(userId || quota?.verified) && (
          <button
            type="button"
            onClick={handleLogout}
            style={{
              ...controlStyle,
              border: "1px solid rgba(255,255,255,0.6)",
              background: "transparent",
              color: "#fff",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Sign out
          </button>
        )}
      </div>

      {/* ===================== SEARCH BAR ===================== */}
      <form
        onSubmit={onSubmit}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 10px",
          background: "#f7f9fc",
          borderBottom: "1px solid #dfe4ea",
          flexWrap: "wrap",
        }}
      >
        {advanced && (
        <select
          value={source}
          onChange={(e) => {
            setSource(e.target.value);
            setRows([]);
            setHasSearched(false);
            // The reason control is hidden on the draft roll, so clear it —
            // otherwise an invisible filter would still be armed on the way back.
            setReason("");
            closeDetail();
          }}
          title="Which SIR dataset to search"
          style={{ ...controlStyle, minWidth: 210, fontWeight: 700 }}
        >
          {SIR_SOURCES.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        )}

        <div
          style={{
            position: "relative",
            // With the filters hidden this is the only field on the row, so it
            // takes the width instead of sitting in a 240px box.
            flex: advanced ? "0 1 240px" : "1 1 340px",
            minWidth: 180,
          }}
        >
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={
              advanced
                ? "Name, EPIC, 141/12/332 or 12/332…"
                : "Type your name or voter ID…"
            }
            onKeyDown={(e) => {
              // Shift+Enter searches from anywhere in the box, as requested.
              if (e.key === "Enter" && e.shiftKey) {
                e.preventDefault();
                runSearch();
              }
            }}
            title="Enter or Shift+Enter to search · Esc to clear"
            style={{
              ...controlStyle,
              width: "100%",
              paddingRight: 30,
              // 16px is the threshold below which iOS zooms into the field and
              // leaves the page scrolled sideways.
              fontSize: narrow ? 16 : 13.5,
              height: narrow ? 44 : 32,
            }}
          />
          {query && (
            <span
              onClick={() => {
                setQuery("");
                inputRef.current?.focus();
              }}
              title="Clear text"
              style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
                color: "#666",
                fontWeight: "bold",
                userSelect: "none",
              }}
            >
              ✕
            </span>
          )}
        </div>

        {advanced && (
        <select
          value={yadibhag}
          onChange={(e) => setYadibhag(e.target.value)}
          title="Part / booth"
          style={{
            ...controlStyle,
            // A 320px floor overflows a 360px phone once padding is counted,
            // which is what pushed the whole search bar sideways.
            ...(narrow
              ? { flex: "1 1 100%", minWidth: 0, width: "100%" }
              : { flex: "1 1 420px", minWidth: 320, maxWidth: 620 }),
          }}
        >
          <option value="">All parts</option>
          {activeParts.map((p) => {
            const n = source === "draft" ? p.loadedCount : p.voterCount;
            return (
              <option key={p.yadibhag} value={p.yadibhag}>
                {p.yadibhag} — {p.PartName || p.PollingStation || "(no name)"}
                {n != null ? ` (${n})` : ""}
              </option>
            );
          })}
        </select>
        )}

        {/* Reasons are a property of the enumeration list; the draft roll has
            no such column, so the control is hidden rather than left dead. */}
        {advanced && source === "flagged" && (
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            title="Deletion reason"
            style={{ ...controlStyle, minWidth: 175 }}
          >
            {SIR_REASONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        )}

        <button
          type="submit"
          disabled={loading}
          style={{
            ...controlStyle,
            background: loading ? "#9bb8d8" : SIR_THEME.bar,
            color: "#fff",
            border: "none",
            fontWeight: 700,
            cursor: loading ? "wait" : "pointer",
            minWidth: narrow ? 104 : 92,
            height: narrow ? 44 : 32,
            fontSize: narrow ? 15 : 12.5,
          }}
        >
          {loading ? "Searching…" : "Search"}
        </button>

        <button
          type="button"
          onClick={clearAll}
          style={{
            ...controlStyle,
            background: "#f1f3f5",
            cursor: "pointer",
            height: narrow ? 44 : 32,
          }}
        >
          Reset
        </button>
      </form>

      {/* ===================== ALLOWANCE NOTICE =====================
        * Shown INSTEAD of a blocking dialog when the server refuses a voter.
        * Searching the list is still free, so everything below stays usable —
        * this only explains what happened and offers the action that fixes it.
        */}
      {quotaBlock && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "5px 10px",
            background: quotaBlock.reason === "needCredit" ? "#fdecea" : "#fff8e1",
            borderBottom:
              "1px solid " +
              (quotaBlock.reason === "needCredit" ? "#f5c6c0" : "#ffe0a3"),
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              width: 9,
              height: 9,
              borderRadius: "50%",
              flex: "0 0 auto",
              background: quotaBlock.reason === "needCredit" ? "#c62828" : "#f0a202",
            }}
          />

          <span
            style={{
              fontSize: 12.5,
              color: "#33475b",
              fontWeight: 600,
              flex: 1,
              minWidth: 220,
              lineHeight: 1.35,
            }}
          >
            {quotaBlockMessage(quotaBlock)}
          </span>

          {(quotaBlock.reason === "needOtp" || quotaBlock.reason === "needCredit") && (
            <button
              type="button"
              onClick={() => setShowAccess(true)}
              style={{
                ...controlStyle,
                background:
                  quotaBlock.reason === "needCredit" ? "#c62828" : SIR_THEME.bar,
                color: "#fff",
                border: "none",
                fontWeight: 700,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {quotaBlock.reason === "needCredit"
                ? "Buy credits"
                : "Verify mobile number"}
            </button>
          )}

          <button
            type="button"
            onClick={() => setQuotaBlock(null)}
            aria-label="Dismiss"
            style={{
              background: "transparent",
              border: "none",
              color: "#8a97a6",
              fontSize: 20,
              lineHeight: 1,
              cursor: "pointer",
            }}
          >
            &times;
          </button>
        </div>
      )}

      {/* ====== REASON CHIPS (flagged list only) / DATA WARNING ======
        * The dataset label and headline count now live in the header bar; this
        * strip renders only when it actually has something to say.
        */}
      {advanced &&
        (source === "flagged" || (source === "draft" && draftTotal === 0)) && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 10px",
            background: "#eef3fa",
            borderBottom: "1px solid #e3e8ef",
            flexWrap: "wrap",
            fontSize: 11.5,
          }}
        >
          {source === "draft" && draftTotal === 0 && (
            <span style={{ color: "#b3261e", fontWeight: 600 }}>
              No draft-roll data loaded yet.
            </span>
          )}

          {source === "flagged" &&
            reasonCounts.map((rc) => {
              const isNone = !rc.Reason || rc.Reason === "(none)";
              const meta = reasonMeta(isNone ? null : rc.Reason);
              const value = isNone ? "" : rc.Reason;
              const active = reason === value && !isNone;

              return (
                <button
                  key={rc.Reason || "(none)"}
                  type="button"
                  disabled={isNone}
                  onClick={() => {
                    setReason(value);
                    runSearch({ reason: value });
                  }}
                  title={isNone ? "Enumerated, no deletion reason" : `Filter by ${meta.label}`}
                  style={{
                    border: `1px solid ${meta.color}`,
                    background: active ? meta.color : "#fff",
                    color: active ? "#fff" : meta.color,
                    borderRadius: 11,
                    padding: "1px 9px",
                    fontWeight: 700,
                    fontSize: 11,
                    cursor: isNone ? "default" : "pointer",
                    opacity: isNone ? 0.65 : 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {meta.label}: {rc.cnt}
                </button>
              );
            })}
        </div>
      )}

      {/* ===================== MESSAGES ===================== */}
      {(error || notice) && (
        <div
          style={{
            margin: "6px 10px 0",
            padding: "6px 10px",
            borderRadius: 6,
            fontSize: 12.5,
            fontWeight: 600,
            background: error ? "#fdecea" : "#fff8e1",
            color: error ? "#b3261e" : "#7a5c00",
            border: `1px solid ${error ? "#f5c2bd" : "#ffe08a"}`,
          }}
        >
          {error || notice}
        </div>
      )}

      {/* ===================== RESULTS ===================== */}
      <div style={{ flex: 1, overflow: "auto", padding: 8, minHeight: 0 }}>
        {loading && (
          <div style={{ padding: 16, textAlign: "center", color: "#555" }}>
            Searching SIR records…
          </div>
        )}

        {!loading && rows.length === 0 && !error && (
          <div
            style={{
              padding: 28,
              textAlign: "center",
              color: "#7b8794",
              fontSize: 13,
            }}
          >
            {hasSearched ? (
              <>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#5a6672" }}>
                  No voter matched this search.
                </div>
                <div style={{ marginTop: 6, lineHeight: 1.6 }}>
                  Try only your first name, or the surname on its own — spellings
                  in the roll often differ. Your voter ID (EPIC) always works.
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#33475b" }}>
                  Check your name on the SIR 2026 roll
                </div>
                <div style={{ marginTop: 6, lineHeight: 1.6 }}>
                  AC {SIR_ASSEMBLY_NAME}. Type your name or your voter ID (EPIC)
                  above and press Search, then tap your name to see your part,
                  serial number and polling station.
                </div>
              </>
            )}
          </div>
        )}

        {/* ---------- MOBILE: one card per voter ----------
            A 10-column table on a 360px screen truncates every field to two
            characters ("Rahul Ail...", "Rajkum...", "F...", "2."), which is
            unreadable - and most voters look themselves up on a phone. Cards
            give each field the full width of the screen instead. */}
        {!loading && rows.length > 0 && useCards && (
          <div
            style={
              narrow
                ? { display: "flex", flexDirection: "column", gap: 8 }
                : {
                    // Desktop gets the same cards, laid out in columns so a
                    // wide screen is not one long ribbon of full-width rows.
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(330px, 1fr))",
                    gap: 10,
                  }
            }
          >
            {pageSlice.map((row, i) => {
              const rowIndex = pageIndex * SIR_PAGE_SIZE + i;
              const isActive = rowIndex === activeIndex;
              const isSelected =
                selectedRow &&
                (row?.SIRId != null
                  ? row.SIRId === selectedRow.SIRId
                  : row === selectedRow);

              const txt = (v) =>
                v == null || v === "" ? null : String(v);
              const name = txt(row.ElectorName) || "(name not read)";
              const rel = txt(row.RelativeName);
              const relation = txt(row.Relation);
              const age = txt(row.age);
              const sex = txt(row.sex);
              const house = txt(row.HouseNumber);

              return (
                <div
                  key={row?.SIRId ?? `${row?.CardNo || ""}-${i}`}
                  data-rowidx={rowIndex}
                  onClick={() => {
                    setActiveIndex(rowIndex);
                    selectRow(row);
                  }}
                  style={{
                    background: isSelected ? "#eaf3ff" : "#fff",
                    border: `1px solid ${isSelected ? SIR_THEME.bar : "#e3e8ef"}`,
                    borderLeft: `4px solid ${
                      isSelected || isActive ? SIR_THEME.bar : "#e3e8ef"
                    }`,
                    borderRadius: 8,
                    padding: "10px 12px",
                    cursor: "pointer",
                    boxShadow: "0 1px 3px rgba(16,42,73,0.08)",
                  }}
                >
                  {/* Name is what people scan for, so it leads and gets the
                      largest type on the card. */}
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 700,
                      color: "#12263f",
                      lineHeight: 1.25,
                      marginBottom: 4,
                    }}
                  >
                    {name}
                  </div>

                  {/* EPIC is the number people are asked for at the booth. */}
                  <div
                    style={{
                      fontFamily: "ui-monospace, Menlo, Consolas, monospace",
                      fontSize: 14,
                      fontWeight: 700,
                      letterSpacing: 0.4,
                      color: row.CardNo ? "#0b5ed7" : "#a01722",
                      marginBottom: 6,
                    }}
                  >
                    {row.CardNo || "EPIC not readable"}
                  </div>

                  {rel && (
                    <div style={{ fontSize: 13.5, color: "#3d4b5c", marginBottom: 2 }}>
                      <span style={{ color: "#7b8794" }}>
                        {relation ? `${relation}: ` : "Relative: "}
                      </span>
                      {rel}
                    </div>
                  )}

                  <div style={{ fontSize: 13.5, color: "#3d4b5c", marginBottom: 2 }}>
                    {[age && `Age ${age}`, sex, house && `House ${house}`]
                      .filter(Boolean)
                      .join("  ·  ")}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap",
                      marginTop: 6,
                    }}
                  >
                    <span
                      style={{
                        background: "#eef2f7",
                        color: "#3d4b5c",
                        borderRadius: 12,
                        padding: "2px 10px",
                        fontSize: 12.5,
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Part {row.yadibhag ?? "-"} &nbsp;•&nbsp; Sr {row.vno ?? "-"}
                    </span>

                    {source === "flagged" && (
                      <ReasonBadge value={row?.[SIR_REASON_FIELD]} />
                    )}

                    <span style={{ flex: 1 }} />
                    <span style={{ fontSize: 12, color: SIR_THEME.bar, fontWeight: 700 }}>
                      View details ›
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && rows.length > 0 && !useCards && (
          <div
            style={{
              background: "#fff",
              borderRadius: 8,
              overflow: "auto",
              border: "1px solid #e3e8ef",
              boxShadow: "0 1px 3px rgba(16,42,73,0.08)",
            }}
          >
            <table
              ref={tableRef}
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 12,
                tableLayout: "fixed",
              }}
            >
              <colgroup>
                {columns.map((c) => (
                  <col key={c} style={{ width: SIR_COLUMN_WIDTHS[c] || "auto" }} />
                ))}
              </colgroup>

              <thead>
                <tr>
                  {columns.map((c) => (
                    <th
                      key={c}
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 2,
                        background: "#eef3fa",
                        color: "#33475b",
                        textAlign: "left",
                        padding: "4px 8px",
                        borderBottom: "2px solid #ccd4df",
                        whiteSpace: "nowrap",
                        fontWeight: 700,
                        letterSpacing: 0.2,
                      }}
                    >
                      {SIR_COLUMN_LABELS[c] || c}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {pageSlice.map((row, i) => {
                  const isSelected =
                    selectedRow &&
                    (row?.SIRId != null
                      ? row.SIRId === selectedRow.SIRId
                      : row === selectedRow);

                  // Index into `rows`, not into the page — the keyboard cursor
                  // counts across pages.
                  const rowIndex = pageIndex * SIR_PAGE_SIZE + i;
                  const isActive = rowIndex === activeIndex;

                  return (
                  <tr
                    key={row?.SIRId ?? `${row?.CardNo || ""}-${i}`}
                    data-rowidx={rowIndex}
                    onClick={() => {
                      setActiveIndex(rowIndex);
                      selectRow(row);
                    }}
                    title="Show full SIR 2026 voter details"
                    style={{
                      borderBottom: "1px solid #eef1f5",
                      cursor: "pointer",
                      // Selected (open in the panel) outranks merely highlighted,
                      // so the operator can see both where they are and what is
                      // actually open after arrowing away from it.
                      background: isSelected
                        ? "#e3f0ff"
                        : isActive
                        ? "#f0f4f9"
                        : i % 2
                        ? "#fafbfd"
                        : "#fff",
                      boxShadow: isActive
                        ? `inset 3px 0 0 ${SIR_THEME.accent}`
                        : "none",
                    }}
                  >
                    {columns.map((c) => {
                      if (c === SIR_REASON_FIELD) {
                        return (
                          <td key={c} style={{ padding: "3px 8px" }}>
                            <ReasonBadge value={row?.[c]} />
                          </td>
                        );
                      }

                      const value = row?.[c];
                      const text =
                        value == null || value === "" ? "-" : String(value);

                      return (
                        <td
                          key={c}
                          title={text}
                          style={{
                            padding: "3px 8px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {text}
                        </td>
                      );
                    })}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ============ VOTER DETAIL (SIR 2026 only) ============ */}
      {selectedRow && narrow && (
        <div
          onClick={closeDetail}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(16,42,73,0.35)",
            zIndex: 1199,
          }}
        />
      )}
      {selectedRow && (
        <div
          style={{
            background: "#fff",
            borderTop: `3px solid ${SIR_THEME.bar}`,
            padding: narrow ? "10px 12px 16px" : "6px 10px",
            // On a phone 38vh of a docked strip is barely three lines once the
            // mobile box is in it, so the details rise as a proper sheet over
            // the results instead of fighting them for the fold.
            ...(narrow
              ? {
                  position: "fixed",
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 1200,
                  maxHeight: "82vh",
                  borderTopLeftRadius: 14,
                  borderTopRightRadius: 14,
                  boxShadow: "0 -6px 24px rgba(16,42,73,0.28)",
                }
              : { maxHeight: "38vh" }),
            overflowY: "auto",
            fontSize: narrow ? 13.5 : 12.5,
          }}
        >
          {/* Grab handle — the usual signal that a sheet can be dismissed. */}
          {narrow && (
            <div
              onClick={closeDetail}
              style={{
                width: 40,
                height: 4,
                borderRadius: 2,
                background: "#ccd4df",
                margin: "0 auto 8px",
              }}
            />
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 5,
              flexWrap: "wrap",
            }}
          >
            <b style={{ fontSize: narrow ? 15 : 13.5 }}>Voter details</b>
            {(() => {
              // Verdict needs BOTH datasets: flagged-or-not is only half the answer.
              const onDraft = combined ? combined.DraftVno != null : undefined;
              const rsn = combined ? combined.SirReason : selectedRow[SIR_REASON_FIELD];
              const v = sirVerdict({ onDraft, reason: rsn });
              return (
                <span
                  title="Derived from the enumeration list and the published 2026 draft roll"
                  style={{
                    background: v.color, color: "#fff", padding: "3px 10px",
                    borderRadius: 10, fontSize: 11.5, fontWeight: 800, whiteSpace: "nowrap",
                  }}
                >
                  {v.label}
                </span>
              );
            })()}
            <ReasonBadge value={combined ? combined.SirReason : selectedRow[SIR_REASON_FIELD]} />
            {selectedRow.RefEpic && (
              <span style={{ fontSize: 12, color: "#5a6672" }}>
                already enrolled as <b>{selectedRow.RefEpic}</b>
              </span>
            )}
            <span style={{ flex: 1 }} />
            <button
              type="button"
              onClick={closeDetail}
              style={{ ...controlStyle, cursor: "pointer" }}
            >
              Close
            </button>
          </div>


          {/* SIR 2026 data only — the old campaign roll is never read here. */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: narrow
                ? "1fr 1fr"
                : "repeat(auto-fit, minmax(260px, 1fr))",
              gap: narrow ? "10px 14px" : "6px 18px",
            }}
          >
            <Detail label="EPIC / Voter ID" value={selectedRow.CardNo} strong />
            <Detail
              label="Name"
              value={combined?.DraftName || selectedRow.ElectorName}
              strong
              wide={narrow}
            />
            <Detail
              label="Relative"
              value={
                [
                  combined?.DraftRelative || selectedRow.RelativeName,
                  (combined?.DraftRelation || selectedRow.Relation) &&
                    `(${combined?.DraftRelation || selectedRow.Relation})`,
                ]
                  .filter(Boolean)
                  .join(" ")
              }
            />
            <Detail
              label="Age / Sex"
              value={[
                combined?.DraftAge != null ? combined.DraftAge : selectedRow.age,
                combined?.DraftSex || selectedRow.sex,
              ]
                .filter((v) => v != null && v !== "")
                .join(" / ")}
            />
            {/* Mobile capture + WhatsApp share. Spans the grid because the
                controls need the room. */}
            <div style={{ gridColumn: "1 / -1", marginTop: 4 }}>
              <div style={{ fontSize: 11, color: "#5a6672", marginBottom: 3 }}>
                Mobile number for sharing · visible only to you
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <input
                  value={mobile}
                  onChange={onMobileChange}
                  inputMode="numeric"
                  maxLength={SIR_MOBILE_LENGTH}
                  placeholder={`${SIR_MOBILE_LENGTH} digit mobile number`}
                  type="tel"
                  autoComplete="tel"
                  style={{
                    ...controlStyle,
                    width: narrow ? "100%" : 190,
                    fontSize: narrow ? 16 : 12.5,
                    height: narrow ? 44 : 30,
                    letterSpacing: 1,
                    fontWeight: 700,
                    borderColor: mobile && !mobileIsComplete ? "#dc3545" : "#c7ced6",
                  }}
                />

                {/* One button. A new number is saved as part of sharing — there is
                    no separate Save step to forget. */}
                <button
                  type="button"
                  onClick={shareOnWhatsApp}
                  disabled={!mobileIsComplete || mobileBusy}
                  style={{
                    ...controlStyle,
                    cursor: !mobileIsComplete || mobileBusy ? "not-allowed" : "pointer",
                    background: "#25D366",
                    borderColor: "#1da851",
                    color: "#fff",
                    fontWeight: 700,
                    flex: narrow ? "1 1 160px" : undefined,
                    height: narrow ? 44 : 30,
                    opacity: !mobileIsComplete || mobileBusy ? 0.55 : 1,
                  }}
                  title="Saves the number against this voter, then opens WhatsApp with the details ready to send"
                >
                  {mobileBusy ? "Saving…" : "Share on WhatsApp"}
                </button>

                {/* Same slip, same gate — for pasting into a chat by hand
                    instead of opening WhatsApp once per voter. */}
                <button
                  type="button"
                  onClick={copyDetails}
                  disabled={!mobileIsComplete || mobileBusy}
                  style={{
                    ...controlStyle,
                    cursor: !mobileIsComplete || mobileBusy ? "not-allowed" : "pointer",
                    background: copied ? "#146c43" : "#fff",
                    borderColor: copied ? "#146c43" : "#c7ced6",
                    color: copied ? "#fff" : "#33475b",
                    fontWeight: 700,
                    flex: narrow ? "1 1 120px" : undefined,
                    height: narrow ? 44 : 30,
                    opacity: !mobileIsComplete || mobileBusy ? 0.55 : 1,
                  }}
                  title={
                    mobileIsComplete
                      ? "Saves the number against this voter, then copies the same details to the clipboard"
                      : `Enter the voter's ${SIR_MOBILE_LENGTH} digit mobile number first`
                  }
                >
                  {copied ? "Copied ✓" : "📋 Copy details"}
                </button>

                {mobileIsSaved && !mobileBusy && (
                  <span style={{ fontSize: 12, color: "#146c43", fontWeight: 600 }}>
                    ✓ saved for this voter
                  </span>
                )}

                {mobile && !mobileIsComplete && (
                  <span style={{ fontSize: 12, color: "#dc3545" }}>
                    {SIR_MOBILE_LENGTH - mobile.length} more digit
                    {SIR_MOBILE_LENGTH - mobile.length === 1 ? "" : "s"}
                  </span>
                )}
              </div>

              {mobileNote && (
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: mobileNote.kind === "error" ? "#a01722" : "#146c43",
                  }}
                >
                  {mobileNote.text}
                </div>
              )}
            </div>
            {/* Hidden when the 2026 draft line below already shows the same
                part / serial — no point printing the identical pair twice. */}
            {!(combined &&
              combined.DraftVno != null &&
              String(combined.DraftPart) === String(selectedRow.yadibhag) &&
              String(combined.DraftVno) === String(selectedRow.vno)) && (
              <Detail
                label="SIR part / serial"
                value={`${selectedRow.yadibhag ?? "-"} / ${selectedRow.vno ?? "-"}`}
              />
            )}
            <Detail
              label="2026 draft part / voter no"
              value={combined && combined.DraftVno != null
                ? `${combined.DraftPart} / ${combined.DraftVno}`
                : (combined ? "not on the 2026 roll" : null)}
              strong
            />
            {/* One line, boilerplate/lat-lon/auxiliary-count stripped — the
                building is what the voter actually needs. */}
            <Detail
              label="2026 polling station"
              value={
                sirShortStation(combined?.StationAddress) ||
                sirShortStation(combined?.PollingStation)
              }
              wide
            />
          </div>
        </div>
      )}

      <SirPartsReport
        show={showParts}
        onClose={() => setShowParts(false)}
        parts={parts}
        draftParts={draftParts}
      />

      {/* ============ SUPER-ADMIN MOBILE REGISTER ============ */}
      {showRegister && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 6,
              width: "min(1000px, 100%)",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 12px",
                borderBottom: "1px solid #dfe4ea",
                flexWrap: "wrap",
              }}
            >
              <b style={{ fontSize: 14 }}>Voter mobile register</b>
              <span style={{ fontSize: 12, color: "#5a6672" }}>
                {registerLoading ? "loading…" : `${registerRows.length} entries`}
              </span>

              <span style={{ flex: 1 }} />

              <input
                value={registerSearch}
                onChange={(e) => setRegisterSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") loadRegister(registerSearch);
                }}
                placeholder="Mobile, EPIC or name"
                style={{ ...controlStyle, width: 220 }}
              />
              <button
                type="button"
                onClick={() => loadRegister(registerSearch)}
                style={{ ...controlStyle, cursor: "pointer", fontWeight: 600 }}
              >
                Search
              </button>
              <button
                type="button"
                onClick={exportRegisterCsv}
                disabled={!registerRows.length}
                style={{
                  ...controlStyle,
                  cursor: registerRows.length ? "pointer" : "not-allowed",
                  opacity: registerRows.length ? 1 : 0.5,
                }}
              >
                Export CSV
              </button>
              <button
                type="button"
                onClick={() => setShowRegister(false)}
                style={{ ...controlStyle, cursor: "pointer" }}
              >
                Close
              </button>
            </div>

            <div style={{ overflow: "auto", padding: "0 12px 12px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ position: "sticky", top: 0, background: "#f1f4f7" }}>
                    {["Mobile", "Used by", "EPIC", "Name", "Part / No", "Saved by", "When", ""].map(
                      (h) => (
                        <th
                          key={h}
                          style={{
                            textAlign: "left",
                            padding: "8px 6px",
                            borderBottom: "1px solid #dfe4ea",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {!registerLoading && !registerRows.length && (
                    <tr>
                      <td colSpan={8} style={{ padding: 16, color: "#5a6672" }}>
                        No mobile numbers collected yet.
                      </td>
                    </tr>
                  )}

                  {registerRows.map((r) => {
                    // How many voters this number serves, counted over what is
                    // on screen — the cap itself is enforced server-side.
                    const shared = registerRows.filter(
                      (o) => o.MobileNumber === r.MobileNumber
                    ).length;
                    return (
                      <tr key={r.SIRMobileId} style={{ borderBottom: "1px solid #eef1f4" }}>
                        <td style={{ padding: "6px", fontWeight: 700, whiteSpace: "nowrap" }}>
                          {r.MobileNumber}
                        </td>
                        <td style={{ padding: "6px" }}>
                          <span
                            style={{
                              background: shared >= SIR_MOBILE_MAX_VOTERS ? "#fff3cd" : "#e7f5ec",
                              color: shared >= SIR_MOBILE_MAX_VOTERS ? "#7a5c00" : "#146c43",
                              padding: "2px 8px",
                              borderRadius: 10,
                              fontSize: 11.5,
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {shared} / {SIR_MOBILE_MAX_VOTERS}
                          </span>
                        </td>
                        <td style={{ padding: "6px", whiteSpace: "nowrap" }}>{r.CardNo}</td>
                        <td style={{ padding: "6px" }}>{r.ElectorName || "-"}</td>
                        <td style={{ padding: "6px", whiteSpace: "nowrap" }}>
                          {r.yadibhag ?? "-"} / {r.vno ?? "-"}
                        </td>
                        <td style={{ padding: "6px", whiteSpace: "nowrap" }}>
                          {r.UserFullName || r.UserId || "-"}
                        </td>
                        <td style={{ padding: "6px", whiteSpace: "nowrap", color: "#5a6672" }}>
                          {r.DateTime ? String(r.DateTime).replace("T", " ").slice(0, 16) : "-"}
                        </td>
                        <td style={{ padding: "6px" }}>
                          <button
                            type="button"
                            onClick={() => removeRegisterRow(r)}
                            style={{
                              ...controlStyle,
                              cursor: "pointer",
                              color: "#a01722",
                              padding: "2px 8px",
                              height: 26,
                            }}
                            title="Remove this entry and free the number for another voter"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ===================== FOOTER ===================== */}
      <div
        style={{
          background: "#fff",
          borderTop: "1px solid #dfe4ea",
          padding: "8px 12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: 13,
          gap: 10,
        }}
      >
        <span style={{ fontWeight: 600 }}>Smart Search - 9767728530</span>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {totalPages > 1 && (
            <>
              <button
                type="button"
                onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                disabled={pageIndex === 0}
                style={{ ...controlStyle, cursor: "pointer" }}
              >
                Prev
              </button>
              <span>
                {pageIndex + 1}/{totalPages}
              </span>
              <button
                type="button"
                onClick={() =>
                  setPageIndex((p) => Math.min(totalPages - 1, p + 1))
                }
                disabled={pageIndex >= totalPages - 1}
                style={{ ...controlStyle, cursor: "pointer" }}
              >
                Next
              </button>
            </>
          )}
          <span style={{ whiteSpace: "nowrap" }}>Found: {rows.length}</span>
        </div>
      </div>

      {/* ===================== ACCESS WALL ===================== *
        * Opens when the server refuses a lookup, and from the allowance badge.
        * It never decides entitlement itself — see sirQuotaClient.js. */}
      {/* ===================== MY SEARCHES =====================
        * The permanent record for this number, from SIRSearchAccess. Every
        * search and every voter opened is in here, repeats included — nothing
        * is ever removed, so a paying user can always account for what they
        * spent.
        */}
      {showHistory && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            zIndex: 2000,
          }}
          onClick={() => setShowHistory(false)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 14,
              width: "100%",
              maxWidth: 760,
              maxHeight: "85vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxShadow: "0 18px 50px rgba(0,0,0,0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                background: SIR_THEME.bar,
                color: SIR_THEME.barText,
                padding: "13px 16px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 10,
              }}
            >
              <strong style={{ fontSize: 15.5 }}>My searches</strong>
              <button
                onClick={() => setShowHistory(false)}
                aria-label="Close"
                style={{
                  background: "transparent",
                  border: "none",
                  color: SIR_THEME.barText,
                  fontSize: 22,
                  lineHeight: 1,
                  cursor: "pointer",
                }}
              >
                &times;
              </button>
            </div>

            {history && (
              <div
                style={{
                  display: "flex",
                  gap: 16,
                  flexWrap: "wrap",
                  padding: "10px 16px",
                  background: "#f7f9fc",
                  borderBottom: "1px solid #e3e8ef",
                  fontSize: 12.5,
                }}
              >
                <span>
                  <b>{history.Name || history.MobileNumber}</b>
                  {history.Name ? " (" + history.MobileNumber + ")" : ""}
                </span>
                <span>
                  Voters opened: <b>{history.votersOpened}</b>
                </span>
                <span>
                  Credits left: <b>{history.credits}</b>
                </span>
              </div>
            )}

            <div style={{ overflow: "auto", padding: "0 16px 16px" }}>
              {historyLoading && (
                <p style={{ fontSize: 13, color: "#8a97a6" }}>Loading&hellip;</p>
              )}
              {historyError && (
                <p style={{ fontSize: 13, color: "#a4271c" }}>{historyError}</p>
              )}

              {!historyLoading && !historyError && history && (
                history.history.length === 0 ? (
                  <p style={{ fontSize: 13, color: "#8a97a6" }}>
                    Nothing searched yet.
                  </p>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                    <thead>
                      <tr style={{ textAlign: "left", color: "#5a6672" }}>
                        <th style={{ padding: "8px 6px", borderBottom: "2px solid #e3e8ef" }}>When</th>
                        <th style={{ padding: "8px 6px", borderBottom: "2px solid #e3e8ef" }}>What</th>
                        <th style={{ padding: "8px 6px", borderBottom: "2px solid #e3e8ef" }}>EPIC</th>
                        <th style={{ padding: "8px 6px", borderBottom: "2px solid #e3e8ef" }}>Charged</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.history.map((h) => (
                        <tr key={h.SIRAccessId}>
                          <td style={{ padding: "7px 6px", borderBottom: "1px solid #eef2f7", whiteSpace: "nowrap" }}>
                            {String(h.DateTime || "").replace("T", " ").slice(0, 16)}
                          </td>
                          <td style={{ padding: "7px 6px", borderBottom: "1px solid #eef2f7" }}>
                            {h.Kind === "voter"
                              ? h.ElectorName || "(voter opened)"
                              : "Search: " + (h.QueryText || "-")}
                          </td>
                          <td style={{ padding: "7px 6px", borderBottom: "1px solid #eef2f7", whiteSpace: "nowrap" }}>
                            {h.CardNo || "-"}
                          </td>
                          <td style={{ padding: "7px 6px", borderBottom: "1px solid #eef2f7", whiteSpace: "nowrap" }}>
                            {/* A repeat view costs nothing, and saying so is the
                                point: it is how a user checks their bill. */}
                            {h.Charged ? (
                              <span style={{ color: "#a4271c", fontWeight: 700 }}>
                                1 {h.Tier === "credit" ? "credit" : h.Tier}
                              </span>
                            ) : (
                              <span style={{ color: "#8a97a6" }}>free</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              )}
            </div>
          </div>
        </div>
      )}

      {showAccess && (
        <SirAccessModal
          quota={quota}
          block={quotaBlock}
          onClose={() => {
            setShowAccess(false);
            setQuotaBlock(null);
          }}
          onVerified={(freshQuota, opts) => {
            setQuota(freshQuota);
            if (opts && opts.keepOpen) return;
            setShowAccess(false);
            setQuotaBlock(null);
            setError("");
            // The voter that was refused is now affordable — reopen it rather
            // than making them find the row again.
            if (selectedRow) selectRow(selectedRow);
          }}
        />
      )}
    </div>
  );
}

export default SirSearchPage;
